import React, { useEffect, useRef, useState } from 'react';
import { AudioLines, ChevronDown, LoaderCircle, X } from 'lucide-react';
import { Button } from '../ui';
import { useAuth } from '../../contexts/AuthContext';
import { apiErrorMessage, ttsService } from '../../services/api';
import './ListenButton.css';

const VOICES = {
  onyx: 'Onyx · masculina, grave',
  echo: 'Echo · masculina, média',
  fable: 'Fable · narrativa',
  alloy: 'Alloy · neutra',
  nova: 'Nova · feminina, suave',
  shimmer: 'Shimmer · feminina, clara',
};

/**
 * "Ouvir" a aula ou o tópico na voz que a pessoa escolheu. O áudio é gerado uma vez por texto e voz
 * (cache no servidor) e em segundo plano: aqui só perguntamos até ficar pronto.
 */
export function ListenButton({ kind, id, available = true }) {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [job, setJob] = useState(null);
  const [error, setError] = useState(null);
  const poll = useRef(null);

  useEffect(() => {
    if (user?.demo) return;
    ttsService.status().then(setStatus).catch(() => setStatus(null));
  }, [user?.demo]);

  useEffect(() => () => clearTimeout(poll.current), []);
  useEffect(() => { setJob(null); setOpen(false); clearTimeout(poll.current); }, [kind, id]);

  const follow = (key) => {
    clearTimeout(poll.current);
    poll.current = setTimeout(async () => {
      try {
        const j = await ttsService.job(key);
        setJob(j);
        if (j.state === 'GENERATING') follow(key);
      } catch {
        setError('Não deu para acompanhar a geração do áudio.');
      }
    }, 3000);
  };

  const request = async () => {
    setError(null);
    try {
      const j = kind === 'lesson' ? await ttsService.lesson(id) : await ttsService.topic(id);
      setJob(j);
      if (j.state === 'GENERATING') follow(j.key);
      if (j.state === 'ERROR') setError(j.error);
    } catch (err) {
      setError(apiErrorMessage(err, 'Não deu para gerar o áudio agora.'));
    }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !job) request();
  };

  const changeVoice = async (voice) => {
    try {
      const r = await ttsService.setVoice(voice);
      setStatus(s => ({ ...s, voice: r.voice }));
      setJob(null);
      request();
    } catch (err) {
      setError(apiErrorMessage(err));
    }
  };

  if (!status?.configured || !available) return null;
  return (
    <span className="listen">
      <Button variant="outline" icon={AudioLines} iconRight={ChevronDown} onClick={toggle} aria-expanded={open}>Ouvir</Button>
      {open && (
        <div className="listen__pop" role="dialog" aria-label="Ouvir">
          <div className="listen__head">
            <strong>Ouvir {kind === 'lesson' ? 'a aula' : 'o tópico'}</strong>
            <button type="button" className="ss-iconbtn" onClick={() => setOpen(false)} aria-label="Fechar"><X size={14} aria-hidden="true" /></button>
          </div>
          {job?.state === 'READY' ? (
            // eslint-disable-next-line jsx-a11y/media-has-caption
            <audio controls autoPlay src={job.url} className="listen__audio" />
          ) : error || job?.state === 'ERROR' ? (
            <p className="listen__error">{error || job.error} <button type="button" onClick={request}>Tentar de novo</button></p>
          ) : (
            <p className="listen__wait"><LoaderCircle size={14} className="listen__spin" aria-hidden="true" />Gerando o áudio... pode levar um minuto na primeira vez.</p>
          )}
          <label className="listen__voice">
            <span>Voz</span>
            <select className="ss-input" value={status.voice} onChange={(e) => changeVoice(e.target.value)}>
              {status.voices.map(v => <option key={v} value={v}>{VOICES[v] || v}</option>)}
            </select>
          </label>
        </div>
      )}
    </span>
  );
}
